import {
  TAssignOrder, TCommissionTeamMember, TLineItem, TMember,
} from '../../types';

interface ICommissionData {
  subTotal: number;
  commissionPlanType: string;
}

interface ICalculateOrderCommissionResult {
  totalCommission: number;
  commissionData: ICommissionData;
}

type TProductCommission = {
  title: string,
  displayName: string,
  variantId: string,
  sku: string,
  fixedType: 'flat' | 'percent',
  fixedAmount: number,
  imageUrl: string,
  price: string,
  compareAtPrice: string,
  category: string
};

function calculateOrderCommissionByPlan(
  subTotal: number | string,
  commissionPlan: any,
  lineItems: Array<TLineItem>,
): ICalculateOrderCommissionResult {
  let totalCommission = 0.00;
  const commissionPlanType = commissionPlan?.type;

  if (commissionPlanType === 'Fixed' && commissionPlan.fixedCommission) {
    const { fixedType, fixedAmount } = commissionPlan.fixedCommission;

    if (fixedType === 'flat') {
      totalCommission = fixedAmount;
    }
    if (fixedType === 'percent') {
      totalCommission = (parseFloat(subTotal as string) / 100) * fixedAmount;
    }
  } else if (commissionPlanType === 'Steps' && commissionPlan.commissionSteps) {
    const commissionSteps = [...commissionPlan.commissionSteps];
    commissionSteps.sort((stepA, stepB) => stepA.index - stepB.index);

    let commissionRate = 0.00;

    for (let i = 0; i < commissionSteps.length; i += 1) {
      const step = commissionSteps[i];
      if (subTotal >= step.from) {
        if (step.isPercentage) {
          if (step.percent) {
            commissionRate = (parseFloat(subTotal as string) / 100) * step.percent;
          }
        } else if (step.amount) {
          commissionRate = step.amount;
        }
      }
    }

    totalCommission = commissionRate;
  } else if (commissionPlanType === 'Products' && commissionPlan.productCommissions?.length && lineItems?.length) {
    // eslint-disable-next-line no-restricted-syntax
    for (const item of lineItems) {
      const matchedCommission = commissionPlan.productCommissions.find(
        (product: TProductCommission) => {
          const variantId = product?.variantId?.split('/').pop();
          return String(variantId) === String(item.variantId);
        },
      );

      // eslint-disable-next-line no-continue
      if (!matchedCommission) continue;

      const fixedAmount = parseFloat(matchedCommission.fixedAmount || '0');
      const quantity = item.quantity || 1;
      const price = parseFloat(item.price || '0');

      if (matchedCommission.fixedType === 'flat') {
        totalCommission += fixedAmount * quantity;
      } else if (matchedCommission.fixedType === 'percent') {
        totalCommission += ((price * quantity) / 100) * fixedAmount;
      }
    }
  }

  return {
    totalCommission: parseFloat(totalCommission.toFixed(2)),
    commissionData: {
      subTotal: parseFloat(<string>subTotal),
      commissionPlanType,
    },
  };
}

const mergeEmployeeRates = (
  employeeRates: Record<number, { salesByEmployee: Record<string, number>;
    commissionByEmployee: Record<string, number> }>,
) => {
  const mergedData = {
    salesByEmployee: {} as Record<string, number>,
    commissionByEmployee: {} as Record<string, number>,
  };

  Object.values(employeeRates ?? {})?.forEach(({ salesByEmployee, commissionByEmployee }) => {
    // Merge salesByEmployee
    Object.entries(salesByEmployee ?? {})?.forEach(([userId, sales]) => {
      mergedData.salesByEmployee[userId] = (mergedData.salesByEmployee[userId] || 0) + sales;
    });

    // Merge commissionByEmployee
    Object.entries(commissionByEmployee ?? {})?.forEach(([userId, commission]) => {
      mergedData.commissionByEmployee[userId] = (mergedData.commissionByEmployee[userId] || 0) + commission;
    });
  });

  return mergedData;
};

const mergeAllLocationsData = (
  channelData: Record<number, { seriesData: Array<{ name: string; data: number[] }>;
    sourceNames: string[] }>,
) => {
  const mergedData: { seriesData: Array<{ name: string; data: number[] }>; sourceNames: string[] } = {
    seriesData: [],
    sourceNames: [],
  };

  Object.values(channelData).forEach((locationData) => {
    locationData.sourceNames.forEach((sourceName) => {
      if (!mergedData.sourceNames.includes(sourceName)) {
        mergedData.sourceNames.push(sourceName);
      }
    });

    locationData.seriesData.forEach((series) => {
      let existingSeries = mergedData.seriesData.find((s) => s.name === series.name);
      if (!existingSeries) {
        existingSeries = { name: series.name, data: Array(mergedData.sourceNames.length).fill(0) };
        mergedData.seriesData.push(existingSeries);
      }

      series.data.forEach((value, index) => {
        const sourceIndex = mergedData.sourceNames.indexOf(locationData.sourceNames[index]);
        if (sourceIndex !== -1) {
          existingSeries.data[sourceIndex] = (existingSeries.data[sourceIndex] || 0) + value;
        }
      });
    });
  });

  return mergedData;
};

const filterUserOrders = <T extends { shopifyId: string | number, commissionTags: Array<string> }>(
  orders: TAssignOrder[],
  user: T,
): TAssignOrder[] => orders.filter((order) => {
    if (String(order.userId).toLowerCase() === String(user.shopifyId).toLowerCase()) {
      return true;
    }

    if (Array.isArray(order.tags) && Array.isArray(user.commissionTags)) {
      return order.tags.some((tag) => user.commissionTags.some((commissionTag) => commissionTag?.toLowerCase() === tag?.toLowerCase()));
    }

    return false;
  });

const filterTeamOrders = (
  orders: TAssignOrder[],
  team: { members: TCommissionTeamMember[] },
): TAssignOrder[] => orders.filter((order) => team.members.some(
  (member) => String(order.userId)?.toLowerCase() === String(member.shopifyId)?.toLowerCase()
        || (
          Array.isArray(order.tags) && Array.isArray(member.commissionTags)
          && order.tags.some((tag) => member.commissionTags.some((commissionTag) => commissionTag?.toLowerCase() === tag?.toLowerCase()))
        ),
));

const filterOrdersWithLocation = (orders: TAssignOrder[], member: TMember, locationId: string) => orders.filter(
  (order) => (String(order.userId) === String(member.shopifyId)
        || (Array.isArray(order.tags)
          && Array.isArray(member.commissionTags)
          && order.tags.some((tag) => member.commissionTags.some((commissionTag) => commissionTag?.toLowerCase() === tag?.toLowerCase()))))
      && order.locationId === Number(locationId),
);

export {
  calculateOrderCommissionByPlan,
  mergeEmployeeRates,
  mergeAllLocationsData,
  filterUserOrders,
  filterTeamOrders,
  filterOrdersWithLocation,
};
